import { env } from '@/lib/config/env'

const TOKEN_KEY = 'refresh_token'

/**
 * Native refresh-token storage boundary (D-025).
 *
 * Mobile shells persist the refresh token in the platform keystore through the
 * in-repo `secure-store` plugin (Android EncryptedSharedPreferences backed by
 * the Android Keystore); browsers never load it and keep using the HttpOnly
 * refresh cookie. Tokens stay in memory after hydration and are never written
 * to localStorage or logged.
 */
export interface RefreshTokenStore {
  /** Hydrates the in-memory token from secure storage. Idempotent. */
  init(): Promise<void>
  get(): string | null
  set(token: string | null): Promise<void>
}

class MemoryRefreshTokenStore implements RefreshTokenStore {
  private token: string | null = null

  async init(): Promise<void> {
    // Nothing to hydrate: cookie sessions do not keep a token in JS.
  }

  get(): string | null {
    return this.token
  }

  async set(token: string | null): Promise<void> {
    this.token = token
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms))
}

class NativeRefreshTokenStore implements RefreshTokenStore {
  private token: string | null = null
  private hydrated = false

  async init(): Promise<void> {
    if (this.hydrated) return
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const { invoke } = await import('@tauri-apps/api/core')
        const response = await invoke<{ value?: string | null }>(
          'plugin:kuniri-secure-store|get',
          { key: TOKEN_KEY },
        )
        this.token = response?.value ?? null
        this.hydrated = true
        return
      } catch {
        // The bridge may not be ready on the very first frame after a cold
        // start; retry once before treating the session as a guest.
        if (attempt === 0) await delay(60)
      }
    }
    // A real keystore failure must not block startup; the user signs in.
    this.token = null
    this.hydrated = true
  }

  get(): string | null {
    return this.token
  }

  async set(token: string | null): Promise<void> {
    this.token = token
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const { invoke } = await import('@tauri-apps/api/core')
        if (token === null) {
          await invoke('plugin:kuniri-secure-store|remove', { key: TOKEN_KEY })
        } else {
          await invoke('plugin:kuniri-secure-store|set', {
            key: TOKEN_KEY,
            value: token,
          })
        }
        return
      } catch {
        if (attempt === 0) await delay(60)
      }
    }
    // Persistence is best-effort: never break login or logout because the
    // keystore is unavailable.
  }
}

// The Android APK is a bearer build even if __TAURI_INTERNALS__ is not visible
// at the exact moment this module is evaluated. Select the keystore store from
// the build configuration as a fallback; a web build has cookie auth, so this
// never activates in a browser.
const nativeShell =
  env.isTauri() ||
  (env.authMode === 'bearer' && env.clientPlatform === 'android')

export const refreshTokenStore: RefreshTokenStore = nativeShell
  ? new NativeRefreshTokenStore()
  : new MemoryRefreshTokenStore()
